// ---------------------------------------------------------------------------
// v1 API client — talks to /api/v1/* on ztd_app/api/main.py, the same
// app apiv2 talks to for /api/v2/* (both namespaces are mounted on one
// FastAPI process; the two clients are separated by URL prefix, not by
// backend process — legacy backend/main.py, which apiv2 previously
// targeted, was retired wholesale in #524 item 7, 2026-08-13).
//
// Vite dev proxy already routes /api/v1/* → http://localhost:8001 per
// frontend/vite.config.ts. Prod nginx applies the same split.
//
// HITL endpoints land here (P9c — ReviewShell + DegradedModeBanner).
// ---------------------------------------------------------------------------

import { clearAuth } from "./auth";

const BASE = "/api/v1";

// Methods the backend's CSRF middleware requires a double-submit token for
// (mirrors SAFE_METHODS in ztd_app/api/core/middleware/csrf.py).
const CSRF_REQUIRED_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? "GET").toUpperCase();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(CSRF_REQUIRED_METHODS.has(method)
      ? {
          "X-CSRF-Token":
            document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
        }
      : {}),
    ...(init?.headers as Record<string, string>),
  };
  const res = await fetch(`${BASE}${url}`, {
    ...init,
    headers,
    credentials: "include",
  });
  if (res.status === 401) {
    clearAuth();
    throw new Error("Session expired — please log in again.");
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(body.detail || `Request failed: ${res.status}`);
  }
  if (res.status === 204) return undefined as unknown as T;
  return res.json();
}

// ---------------------------------------------------------------------------
// HITL types — mirror ztd_app/api/modules/hitl/schemas.py
// ---------------------------------------------------------------------------

export type GateStatus = "pending" | "approved" | "rejected" | "escalated";
export type GateDecision = "approved" | "rejected";
export type AutonomyLevel = "full_review" | "threshold_review" | "autonomous";

export interface GateResponse {
  id: string;
  tenant_id: string;
  created_at: string;
  updated_at: string;
  pipeline_run_id: string;
  stage_id: string;
  /** Project the gated run belongs to; null for a gate that records none (tenant-level). */
  project_id?: string | null;
  agent_id: string;
  confidence_score: number;
  reasoning_chain: Record<string, unknown>;
  input_data_sample: Record<string, unknown> | null;
  output_data_sample: Record<string, unknown> | null;
  affected_row_count: number;
  status: GateStatus;
  reviewer_id: string | null;
  decision_at: string | null;
  notes: string | null;
  timeout_hours: number;
  escalation_level: number;
  pipeline_initiator_id: string | null;
}

export interface GateListResponse {
  gates: GateResponse[];
  total: number;
  cursor: string | null;
}

export interface GateDecisionRequest {
  decision: GateDecision;
  notes?: string | null;
}

export interface CorrectionResponse {
  id: string;
  gate_id: string;
  agent_id: string;
  field_name: string;
  original_value: string | null;
  corrected_value: string | null;
  reasoning: string | null;
  correction_type: string;
  created_at: string;
  updated_at: string;
}

export interface CorrectionCreateRequest {
  field_name: string;
  original_value?: string | null;
  corrected_value?: string | null;
  reasoning?: string | null;
  correction_type?: "value_change" | "addition" | "deletion";
}

// ---------------------------------------------------------------------------
// HITL client (P9c)
// ---------------------------------------------------------------------------

export const hitl = {
  /** List gates. Backend filters by reviewer_id when caller has hitl:read. */
  listGates: (params?: { status?: GateStatus; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set("status", params.status);
    if (params?.limit) q.set("limit", String(params.limit));
    const suffix = q.toString() ? `?${q}` : "";
    return request<GateListResponse>(`/hitl/gates${suffix}`);
  },

  getGate: (gateId: string) => request<GateResponse>(`/hitl/gates/${gateId}`),

  decideGate: (gateId: string, body: GateDecisionRequest) =>
    request<GateResponse>(`/hitl/gates/${gateId}/decision`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  submitCorrection: (gateId: string, body: CorrectionCreateRequest) =>
    request<CorrectionResponse>(`/hitl/gates/${gateId}/corrections`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  listCorrections: (gateId: string) =>
    request<CorrectionResponse[]>(`/hitl/gates/${gateId}/corrections`),
};

// ---------------------------------------------------------------------------
// Quarantine types — mirror ztd_app/api/modules/quarantine/schemas.py
// ---------------------------------------------------------------------------

export type QuarantineStatus = "quarantined" | "released" | "deleted";
export type QuarantineDetectionStage = "primary" | "secondary";

export interface QuarantineItemResponse {
  id: string;
  tenant_id: string;
  created_at: string;
  updated_at: string;
  source_module: string;
  detection_stage: QuarantineDetectionStage;
  original_filename: string;
  size_bytes: number;
  detected_signature: string;
  status: QuarantineStatus;
  reviewer_id: string | null;
  decision_at: string | null;
  notes: string | null;
  uploaded_by: string | null;
}

export interface QuarantineListResponse {
  items: QuarantineItemResponse[];
  total: number;
  cursor: string | null;
}

export interface QuarantineDecisionRequest {
  notes?: string | null;
}

// ---------------------------------------------------------------------------
// Quarantine client — review queue for uploads that failed malware scanning
// ---------------------------------------------------------------------------

export const quarantine = {
  listItems: (params?: {
    status?: QuarantineStatus;
    sourceModule?: string;
    detectionStage?: QuarantineDetectionStage;
    pageSize?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set("status", params.status);
    if (params?.sourceModule) q.set("source_module", params.sourceModule);
    if (params?.detectionStage) q.set("detection_stage", params.detectionStage);
    if (params?.pageSize) q.set("page_size", String(params.pageSize));
    const suffix = q.toString() ? `?${q}` : "";
    return request<QuarantineListResponse>(`/quarantine${suffix}`);
  },

  getItem: (itemId: string) =>
    request<QuarantineItemResponse>(`/quarantine/${itemId}`),

  downloadUrl: (itemId: string) => `${BASE}/quarantine/${itemId}/download`,

  releaseItem: (itemId: string, body?: QuarantineDecisionRequest) =>
    request<QuarantineItemResponse>(`/quarantine/${itemId}/release`, {
      method: "POST",
      body: JSON.stringify(body ?? {}),
    }),

  deleteItem: (itemId: string, body?: QuarantineDecisionRequest) =>
    request<QuarantineItemResponse>(`/quarantine/${itemId}/delete`, {
      method: "POST",
      body: JSON.stringify(body ?? {}),
    }),
};

/**
 * Optional UI-hint shape declared by a skill via `ApprovalStage.gate_ui_hints`.
 * The renderer treats every field as optional and falls back to a generic
 * JSON view when absent or unrecognised. Conventional keys:
 *
 *   highlight_fields: list[str]
 *     Surface specific output_data_sample / input_data_sample fields in
 *     the gate detail header.
 *   severity_color_map: dict[str, string]
 *     Colour severity labels (e.g. {"high": "red", "medium": "amber"}).
 *   primary_action_label: str
 *     Override the default "Approve" button text (e.g. "Approve mask plan").
 *
 * Skills opt in incrementally — unhinted gates still render correctly.
 */
// ---------------------------------------------------------------------------
// Pipeline-runs client (P11a — run pinning + /runs/compare)
// ---------------------------------------------------------------------------

export interface PipelineRunSummary {
  id: string;
  name: string;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  duration_seconds: number | null;
  succeeded_count: number;
  failed_count: number;
  skipped_count: number;
  partial_success: boolean;
  is_pinned: boolean;
  pin_label: string | null;
}

export interface RunCompareDeltas {
  succeeded_delta: number;
  failed_delta: number;
  skipped_delta: number;
  duration_delta_seconds: number | null;
}

export interface StageDiffEntry {
  stage_name: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
}

export interface SchemaDiffColumn {
  name: string;
  data_type: string;
  nullable: boolean;
}

export interface SchemaDiffAddedRemoved {
  table: string;
  column: SchemaDiffColumn;
}

export interface SchemaDiffTypeChange {
  table: string;
  column: string;
  before_type: string;
  after_type: string;
  before_nullable: boolean;
  after_nullable: boolean;
}

export interface SchemaDiffRenameCandidate {
  table: string;
  before_name: string;
  after_name: string;
  data_type: string;
  confidence: number;
}

export interface SchemaDiffTable {
  table: string;
  added: SchemaDiffAddedRemoved[];
  removed: SchemaDiffAddedRemoved[];
  type_changes: SchemaDiffTypeChange[];
  rename_candidates: SchemaDiffRenameCandidate[];
}

export interface SchemaDiffPayload {
  tables_added: string[];
  tables_removed: string[];
  table_diffs: SchemaDiffTable[];
  summary: Record<string, number>;
}

export interface SchemaDiffSection {
  available: boolean;
  reason?: string;
  diff?: SchemaDiffPayload;
}

export interface RunCompareResponse {
  run_a: PipelineRunSummary;
  run_b: PipelineRunSummary;
  deltas: RunCompareDeltas;
  stage_diffs: StageDiffEntry[];
  schema_diff: SchemaDiffSection;
}

interface APIEnvelope<T> {
  data: T;
  meta?: Record<string, unknown>;
}

export const pipelineRuns = {
  /** Compare two pipeline runs side-by-side (P11a AC-P11.3/.5). */
  compare: async (a: string, b: string): Promise<RunCompareResponse> => {
    const q = new URLSearchParams({ a, b });
    const env = await request<APIEnvelope<RunCompareResponse>>(
      `/pipeline-runs/compare?${q}`,
    );
    return env.data;
  },

  /** List pinned baseline runs for the tenant. */
  listPinned: async (): Promise<PipelineRunSummary[]> => {
    const env = await request<APIEnvelope<PipelineRunSummary[]>>(
      `/pipeline-runs/pinned`,
    );
    return env.data;
  },

  /** Pin a run as a reference baseline (P11a AC-P11.6). */
  pin: async (runId: string, label?: string): Promise<PipelineRunSummary> => {
    const suffix = label ? `?label=${encodeURIComponent(label)}` : "";
    const env = await request<APIEnvelope<PipelineRunSummary>>(
      `/pipeline-runs/${runId}/pin${suffix}`,
      { method: "POST" },
    );
    return env.data;
  },

  /** Clear a run's pin (idempotent). */
  unpin: async (runId: string): Promise<PipelineRunSummary> => {
    const env = await request<APIEnvelope<PipelineRunSummary>>(
      `/pipeline-runs/${runId}/pin`,
      { method: "DELETE" },
    );
    return env.data;
  },
};

export interface GateUiHints {
  highlight_fields?: string[];
  severity_color_map?: Record<string, string>;
  primary_action_label?: string;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// RBAC Admin (Story 5.1 AC-6) — role + permission management.
// Mirrors ztd_app/api/modules/auth/rbac_router.py, mounted at
// /api/v1/admin/*. All endpoints require the admin:rbac permission.
// ---------------------------------------------------------------------------

export interface PermissionOut {
  id: string;
  codename: string;
  description: string | null;
  source: string;
  category: string;
  created_at: string;
}

export interface RoleOut {
  id: string;
  name: string;
  description: string | null;
  is_system_role: boolean;
  permissions: string[];
  created_at: string | null;
  updated_at: string | null;
}

export const rbacAdmin = {
  /** List every registered permission codename in the registry. */
  listPermissions: async (): Promise<PermissionOut[]> => {
    const env =
      await request<APIEnvelope<PermissionOut[]>>("/admin/permissions");
    return env.data;
  },

  /** Register a new permission codename (rare — only for gaps like a new agent's routes). */
  createPermission: async (body: {
    codename: string;
    description?: string | null;
    category?: string | null;
  }): Promise<PermissionOut> => {
    const env = await request<APIEnvelope<PermissionOut>>(
      "/admin/permissions",
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
    return env.data;
  },

  /** List every role with its current permission set. */
  listRoles: async (): Promise<RoleOut[]> => {
    const env = await request<APIEnvelope<RoleOut[]>>("/admin/roles");
    return env.data;
  },

  /** Create a new custom role. Starts with zero permissions — grant some via updateRolePermissions. */
  createRole: async (body: {
    name: string;
    description?: string | null;
  }): Promise<RoleOut> => {
    const env = await request<APIEnvelope<RoleOut>>("/admin/roles", {
      method: "POST",
      body: JSON.stringify(body),
    });
    return env.data;
  },

  /** Get a single role with its full permission list. */
  getRole: async (roleId: string): Promise<RoleOut> => {
    const env = await request<APIEnvelope<RoleOut>>(`/admin/roles/${roleId}`);
    return env.data;
  },

  /** Replace a role's entire permission set (not incremental — send the full list). */
  updateRolePermissions: async (
    roleId: string,
    permissions: string[],
  ): Promise<{ role_id: string; permissions: string[] }> => {
    const env = await request<
      APIEnvelope<{ role_id: string; permissions: string[] }>
    >(`/admin/roles/${roleId}/permissions`, {
      method: "PUT",
      body: JSON.stringify({ permissions }),
    });
    return env.data;
  },

  /** Assign a user to one of the roles listed by listRoles(). */
  assignUserRole: async (
    userId: string,
    roleId: string,
  ): Promise<{ user_id: string; role_id: string; status: string }> => {
    const env = await request<
      APIEnvelope<{ user_id: string; role_id: string; status: string }>
    >(`/admin/users/${userId}/role`, {
      method: "PUT",
      body: JSON.stringify({ role_id: roleId }),
    });
    return env.data;
  },
};

// ---------------------------------------------------------------------------
// Conversational AI client (docs/architecture/conversational-ai.md, Phase 1)
// ---------------------------------------------------------------------------

export interface ConversationSessionOut {
  id: string;
  tenant_id: string;
  created_at: string;
  updated_at: string;
  project_id: string;
  user_id: string;
  last_active_at: string | null;
}

// Phase 2 RAG design §6.3 — a citation's `type` names what kind of
// evidence it points at; `ref_id` + `label` are enough to render it and,
// for kb_chunk, to look the source document back up.
export interface ConversationCitation {
  type: "query" | "kb_chunk" | "dq_finding" | "glossary";
  ref_id: string;
  label: string;
}

// Internal enum value is "guessing" (§10 decision 1); the UI renders it
// under the "Unverified" label — see ConfidenceBadge in
// ConversationalAIView.tsx.
export type ConfidenceTier = "certain" | "likely" | "guessing";

export interface ConversationTurnOut {
  id: string;
  tenant_id: string;
  created_at: string;
  updated_at: string;
  session_id: string;
  role: "user" | "assistant";
  content: string;
  resolved_ir: Record<string, unknown> | null;
  compiled_sql: string | null;
  row_policy_applied: string | null;
  result_summary: Record<string, unknown> | null;
  narrative: string | null;
  confidence_tier: ConfidenceTier | null;
  citations: ConversationCitation[] | null;
}

// Phase 2 RAG design §4.1/§7.3 — one uploaded KB grounding document.
export interface ConversationalAIKbDocumentOut {
  id: string;
  tenant_id: string;
  created_at: string;
  updated_at: string;
  project_id: string;
  filename: string;
  doc_type: string;
  classification_tier: number;
  status: "processing" | "ready" | "failed" | "pending_classification_review";
  error_detail: string | null;
  uploaded_by: string;
  uploaded_at: string;
  cleared_by: string | null;
  cleared_at: string | null;
}

export const conversationalAI = {
  /** Start a new conversation, scoped to exactly one project. */
  createSession: async (projectId: string): Promise<ConversationSessionOut> => {
    const env = await request<APIEnvelope<ConversationSessionOut>>(
      "/conversational-ai/sessions",
      {
        method: "POST",
        body: JSON.stringify({ project_id: projectId }),
      },
    );
    return env.data;
  },

  /** Ask a question in an existing session — runs the full guardrail
   * pipeline server-side and returns the persisted assistant turn. */
  ask: async (
    sessionId: string,
    question: string,
    userAttributes?: Record<string, string>,
  ): Promise<ConversationTurnOut> => {
    const env = await request<APIEnvelope<ConversationTurnOut>>(
      `/conversational-ai/sessions/${sessionId}/ask`,
      {
        method: "POST",
        body: JSON.stringify({
          question,
          user_attributes: userAttributes ?? {},
        }),
      },
    );
    return env.data;
  },

  /** Full turn history for a session, oldest first. */
  listTurns: async (sessionId: string): Promise<ConversationTurnOut[]> => {
    const env = await request<APIEnvelope<ConversationTurnOut[]>>(
      `/conversational-ai/sessions/${sessionId}/turns`,
    );
    return env.data;
  },

  // ── Knowledge base (§4, §7.3) ──────────────────────────────────────

  /** Upload one KB grounding document. Bypasses the shared `request()`
   * helper (same reason apiv2.ts's upload* methods do) — a FormData body
   * needs the browser's own multipart Content-Type/boundary, which a
   * forced `application/json` header would break. */
  uploadKbDocument: async (
    projectId: string,
    file: File,
    docType: string,
  ): Promise<ConversationalAIKbDocumentOut> => {
    const formData = new FormData();
    formData.append("project_id", projectId);
    formData.append("doc_type", docType);
    formData.append("file", file);
    const res = await fetch(`${BASE}/conversational-ai/kb/documents`, {
      method: "POST",
      body: formData,
      headers: {
        "X-CSRF-Token":
          document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
      },
      credentials: "include",
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(body.detail || `Upload failed: ${res.status}`);
    }
    const env: APIEnvelope<ConversationalAIKbDocumentOut> = await res.json();
    return env.data;
  },

  listKbDocuments: async (
    projectId: string,
  ): Promise<ConversationalAIKbDocumentOut[]> => {
    const env = await request<APIEnvelope<ConversationalAIKbDocumentOut[]>>(
      `/conversational-ai/kb/documents?project_id=${projectId}`,
    );
    return env.data;
  },

  deleteKbDocument: async (
    documentId: string,
    projectId: string,
  ): Promise<void> => {
    await request<APIEnvelope<{ deleted: boolean }>>(
      `/conversational-ai/kb/documents/${documentId}?project_id=${projectId}`,
      { method: "DELETE" },
    );
  },

  /** Admin-only clearance action for a `pending_classification_review`
   * document (§4.2 step 4). */
  clearKbDocumentReview: async (
    documentId: string,
    projectId: string,
  ): Promise<ConversationalAIKbDocumentOut> => {
    const env = await request<APIEnvelope<ConversationalAIKbDocumentOut>>(
      `/conversational-ai/kb/documents/${documentId}/clear-review?project_id=${projectId}`,
      { method: "POST" },
    );
    return env.data;
  },
};
